// src/HomePage/Game/FolkGame/Caro/CaroIndex.js
import React, { useState, useEffect, useRef } from 'react';
import {
  subscribeFolkRooms,
  subscribeRoomDetail,
  getRoomSnapshot,
  createFolkRoom,
  joinFolkRoom,
  quickJoinFolkRoom,
  leaveFolkRoom,
  togglePlayerReady,
  kickPlayerFromRoom
} from '../FolkGameService';
import CaroLobby from './CaroLobby';
import CaroWaitingRoom from './CaroWaitingRoom';
import CaroGame from './CaroGame';
import { createEmptyBoard } from './CaroLogic';

const BOT_SESSION_KEY = 'active_caro_room_id';
const BOT_DIFFICULTY_KEY = 'caro_bot_difficulty';

const CaroIndex = ({ currentUser, onBackToPortal }) => {
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeRoom, setActiveRoom] = useState(null);
  const [isBotMode, setIsBotMode] = useState(false);

  const activeRoomRef = useRef(activeRoom);
  useEffect(() => {
    activeRoomRef.current = activeRoom;
  }, [activeRoom]);

  // 1. Dọn dẹp phòng khi người dùng đóng Tab trình duyệt (beforeunload)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const room = activeRoomRef.current;
      if (room?.id && room.id !== 'bot_room' && currentUser?.id) {
        // Chủ phòng một mình -> dọn dẹp bàn
        if (room.player1?.id === currentUser.id && !room.player2) {
          leaveFolkRoom('caro', room.id, currentUser.id);
        }
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentUser?.id]);

  // 2. Khôi phục phiên phòng khi F5 tải lại trang
  useEffect(() => {
    const restoreSession = async () => {
      const savedRoomId = sessionStorage.getItem(BOT_SESSION_KEY);
      if (!savedRoomId || !currentUser?.id) return;

      setIsLoading(true);

      if (savedRoomId === 'bot_room') {
        const savedDifficulty = sessionStorage.getItem(BOT_DIFFICULTY_KEY) || 'hard';
        setIsBotMode(true);
        setActiveRoom(buildBotRoom(currentUser, savedDifficulty));
        setIsLoading(false);
        return;
      }

      const roomData = await getRoomSnapshot('caro', savedRoomId);
      if (
        roomData &&
        (roomData.player1?.id === currentUser.id || roomData.player2?.id === currentUser.id)
      ) {
        setActiveRoom(roomData);
      } else {
        sessionStorage.removeItem(BOT_SESSION_KEY);
        sessionStorage.removeItem(BOT_DIFFICULTY_KEY);
      }
      setIsLoading(false);
    };
    restoreSession();
  }, [currentUser?.id]);

  // 3. Lắng nghe danh sách phòng ở Sảnh (đã tích hợp Lazy Filter 10 phút)
  useEffect(() => {
    if (activeRoom) return;
    setIsLoading(true);
    const unsubscribe = subscribeFolkRooms('caro', (roomList) => {
      setRooms(roomList);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [activeRoom]);

  // 4. Lắng nghe chi tiết bàn cờ & Kiểm tra bị Kick hoặc Phòng tự xóa
  useEffect(() => {
    if (!activeRoom?.id || isBotMode) return;

    const unsubscribe = subscribeRoomDetail('caro', activeRoom.id, (updatedRoom) => {
      if (!updatedRoom) {
        sessionStorage.removeItem(BOT_SESSION_KEY);
        setActiveRoom(null);
        setErrorMessage('Bàn chơi đã đóng.');
        return;
      }

      if (
        updatedRoom.kickedPlayerId === currentUser?.id &&
        updatedRoom.player2?.id !== currentUser?.id &&
        updatedRoom.player1?.id !== currentUser?.id
      ) {
        sessionStorage.removeItem(BOT_SESSION_KEY);
        setActiveRoom(null);
        setErrorMessage('Bạn đã bị chủ phòng mời ra khỏi bàn chơi!');
        return;
      }

      setActiveRoom(updatedRoom);
      sessionStorage.setItem(BOT_SESSION_KEY, updatedRoom.id);
    });

    return () => unsubscribe();
  }, [activeRoom?.id, isBotMode, currentUser?.id]);

  const handleCreateRoom = async (name, password) => {
    if (!currentUser?.id) {
      setErrorMessage('Vui lòng đăng nhập để tạo bàn!');
      return;
    }
    setIsLoading(true);
    const result = await createFolkRoom('caro', {
      name,
      password,
      player1: currentUser
    });
    setIsLoading(false);

    if (result.success) {
      setIsBotMode(false);
      setActiveRoom(result.room);
      sessionStorage.setItem(BOT_SESSION_KEY, result.roomId);
      sessionStorage.removeItem(BOT_DIFFICULTY_KEY);
    } else {
      setErrorMessage(result.message || 'Không thể tạo bàn cờ.');
    }
  };

  const handleJoinRoom = async (roomId, password) => {
    if (!currentUser?.id) {
      setErrorMessage('Vui lòng đăng nhập để tham gia!');
      return;
    }
    setIsLoading(true);
    const res = await joinFolkRoom('caro', roomId, currentUser, password);
    setIsLoading(false);

    if (res.success) {
      setIsBotMode(false);
      setActiveRoom(res.room);
      sessionStorage.setItem(BOT_SESSION_KEY, roomId);
      sessionStorage.removeItem(BOT_DIFFICULTY_KEY);
    } else {
      setErrorMessage(res.message);
    }
  };

  const handleQuickMatch = async () => {
    if (!currentUser?.id) {
      setErrorMessage('Vui lòng đăng nhập để ghép bàn tự động!');
      return;
    }
    setIsLoading(true);
    const res = await quickJoinFolkRoom('caro', currentUser);
    setIsLoading(false);

    if (res.success) {
      setIsBotMode(false);
      setActiveRoom(res.room);
      sessionStorage.setItem(BOT_SESSION_KEY, res.room.id);
      sessionStorage.removeItem(BOT_DIFFICULTY_KEY);
    } else {
      setErrorMessage(res.message);
    }
  };

  const handlePlayWithBot = (difficulty = 'hard') => {
    setIsBotMode(true);
    sessionStorage.setItem(BOT_SESSION_KEY, 'bot_room');
    sessionStorage.setItem(BOT_DIFFICULTY_KEY, difficulty);
    setActiveRoom(buildBotRoom(currentUser, difficulty));
  };

  const handleLeaveActiveRoom = async () => {
    if (activeRoom && !isBotMode && currentUser?.id) {
      await leaveFolkRoom('caro', activeRoom.id, currentUser.id);
    }
    sessionStorage.removeItem(BOT_SESSION_KEY);
    sessionStorage.removeItem(BOT_DIFFICULTY_KEY);
    setActiveRoom(null);
    setIsBotMode(false);
  };

  const handleKickPlayer = async () => {
    if (activeRoom && !isBotMode && currentUser?.id) {
      await kickPlayerFromRoom('caro', activeRoom.id, currentUser.id);
    }
  };

  const handleToggleReady = async () => {
    if (!activeRoom || isBotMode || !currentUser?.id) return;
    const isPlayer1 = activeRoom.player1?.id === currentUser.id;
    const currentReady = isPlayer1 ? activeRoom.player1?.ready : activeRoom.player2?.ready;
    await togglePlayerReady('caro', activeRoom.id, isPlayer1, !currentReady);
  };

  if (activeRoom && (activeRoom.status === 'playing' || activeRoom.status === 'finished')) {
    return (
      <CaroGame
        room={activeRoom}
        currentUser={currentUser}
        isBotMode={isBotMode}
        onLeave={handleLeaveActiveRoom}
      />
    );
  }

  if (activeRoom && activeRoom.status === 'waiting') {
    return (
      <CaroWaitingRoom
        room={activeRoom}
        currentUser={currentUser}
        onToggleReady={handleToggleReady}
        onKickPlayer={handleKickPlayer}
        onLeave={handleLeaveActiveRoom}
      />
    );
  }

  return (
    <CaroLobby
      rooms={rooms}
      isLoading={isLoading}
      currentUser={currentUser}
      onCreateRoom={handleCreateRoom}
      onJoinRoom={handleJoinRoom}
      onQuickMatch={handleQuickMatch}
      onPlayWithBot={handlePlayWithBot}
      onBackToPortal={onBackToPortal}
      errorMessage={errorMessage}
      onClearError={() => setErrorMessage('')}
    />
  );
};

function buildBotRoom(currentUser, difficulty) {
  const diffTitles = {
    easy: { name: 'AI Robot (Dễ) 🤖', icon: '🌱' },
    medium: { name: 'AI Robot (Trung Bình) 🤖', icon: '⚔️' },
    hard: { name: 'AI Robot (Cao Thủ) 🤖', icon: '👑' }
  };
  const selectedDiff = diffTitles[difficulty] || diffTitles.hard;

  return {
    id: 'bot_room',
    name: `Luyện tập - ${selectedDiff.name}`,
    botDifficulty: difficulty,
    player1: currentUser || { id: 'guest', fullName: 'Bạn', holyName: '', ready: true },
    player2: {
      id: 'bot',
      fullName: selectedDiff.name,
      holyName: selectedDiff.icon,
      isCatechist: false,
      ready: true
    },
    status: 'playing',
    startingPlayerId: currentUser?.id || 'guest',
    currentTurn: currentUser?.id || 'guest',
    score: { p1: 0, p2: 0, draw: 0 },
    gameState: { board: createEmptyBoard(), lastMove: null, winningLine: [] }
  };
}

export default CaroIndex;